import styled from '@emotion/styled'
import cx from 'classnames'

import { LIST_SIZE } from '@/legacy/blocks/constants'
import {
  Container,
  type BackgroundColor,
  type ContainerProps,
  type Space,
} from '@/legacy/components/Container'
import { ListBlog, type ListBlogProps } from '@/legacy/components/List/Blog'
import { ListDefault, type ListDefaultProps } from '@/legacy/components/List/Default'
import { ListFeatures, type ListFeaturesProps } from '@/legacy/components/List/Features'
import { ListMedias, type ListMediasProps } from '@/legacy/components/List/Medias'
import { ListNews, type ListNewsProps } from '@/legacy/components/List/News'
import { ListPanel, type ListPanelProps } from '@/legacy/components/List/Panel'
import { ListPartner, type ListPartnerProps } from '@/legacy/components/List/Partner'
import { HeadingM, HeadingS, Typography } from '@/legacy/components/Typography'
import { clearBuilderProps } from '@/legacy/core/utils/dom/clearBuilderProps'
import type { TitleProps } from '@/legacy/models'
import { CONFIG } from '@/ui/shared/styles'

import { LIST_LAYOUT, type ListLayoutKeys } from './constants'

export type ListBlockDefaultProps = {
  id?: string
  layout: ListLayoutKeys
  title: TitleProps
  subtitle: TitleProps
  subtitleWeight: 'regular' | 'bold'
  size: string
  position: 'right' | 'center' | 'left'
  background: BackgroundColor
  space: Space
}

export type ListBlockProps = ListBlockDefaultProps &
  ListDefaultProps &
  ListBlogProps &
  ListFeaturesProps &
  ListPartnerProps &
  ListNewsProps &
  ListPanelProps &
  ListMediasProps

const ListBlockStyles = styled(Container)({
  '& .lp-h': {
    marginBottom: '4rem',
  },
  '& .lp-t': {
    marginBottom: '0.5rem',
  },
  '& .subheading': {
    marginTop: '0.5rem',
  },
  [CONFIG.media.mobileLg]: {
    '& .lp-h': {
      marginBottom: '3rem',
    },
  },
})

function ListBlock<T extends ListBlockProps>({
  id,
  size,
  layout,
  position,
  background,
  title,
  subtitle,
  subtitleWeight = 'bold',
  space,
  ...listProps
}: T) {
  const TitleComponent = size === LIST_SIZE.lg ? HeadingM : HeadingS

  clearBuilderProps(listProps)

  const containerProps: ContainerProps = {
    spaceOut: space,
    spaceIn: 'm',
    backgroundColor: background,
    id,
  }
  const hasTitle = title && title.text
  const hasSubtitle = subtitle && subtitle.text

  return (
    <ListBlockStyles {...containerProps}>
      {(hasTitle || hasSubtitle) && (
        <div className={cx('lp-h', { [`et-ta-${position}`]: position })}>
          {hasTitle && (
            <TitleComponent dark="high" tag={title.tag || 'h2'} className="lp-t">
              {title.text}
            </TitleComponent>
          )}
          {hasSubtitle && (
            <Typography
              dark="medium"
              className="subheading"
              tag={subtitle.tag || 'h3'}
              emphasys={subtitleWeight === 'bold'}
            >
              {subtitle.text}
            </Typography>
          )}
        </div>
      )}

      {listProps.items && (
        <>
          {layout === LIST_LAYOUT.list && <ListDefault {...listProps} />}
          {layout === LIST_LAYOUT.blog && <ListBlog {...listProps} />}
          {layout === LIST_LAYOUT.feature && <ListFeatures {...listProps} />}
          {layout === LIST_LAYOUT.news && <ListNews {...listProps} />}
          {layout === LIST_LAYOUT.medias && <ListMedias {...listProps} />}
          {layout === LIST_LAYOUT.panel && <ListPanel {...listProps} />}
          {layout === LIST_LAYOUT.partner && <ListPartner {...listProps} />}
        </>
      )}
    </ListBlockStyles>
  )
}

export default ListBlock
